// src/features/campaigns/components/CampaignsList.tsx
import React, { useState } from 'react'; // Ya no se necesita deleteConfirm
import { MessageSquare, Users, Clock, Archive as ArchiveIcon, Send as SendIcon, Edit3 as EditIcon, RefreshCw, Loader2 } from 'lucide-react';
import { Campaign, CampaignStatus, CampaignType, CampaignFilterStatus } from '../types/campaign';
import { formatDate } from '../../../utils/dates';
import { Calendar, UserMinus, Settings, Trophy, PauseCircle, PlayCircle } from 'lucide-react';
import {
  chipEnvio, etiquetaFicha, puedePausar, puedeReanudar, puedeArchivar, puedeRestaurar,
  ACCIONES, type Tono,
} from '../campaignVocab';
import EmailResultados from './EmailResultados';

// Etiquetas y permisos salen del vocabulario COMPARTIDO con la agenda; el color es local
// porque cada front pinta con su paleta.
const CLASE_TONO: Record<Tono, string> = {
  ok: 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300',
  info: 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300',
  warn: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  error: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
  neutro: 'bg-surface-2 text-content-2',
};

interface CampaignsListProps {
  campaigns: Campaign[];
  onEdit: (campaign: Campaign) => void; // Para "Ver y Enviar" o "Editar Definición"
  // onDelete: (campaignId: string) => Promise<void>; // PROP ELIMINADA
  loading: boolean;
  onArchiveCampaign: (campaignId: string) => Promise<void>;   // FICHA (no toca la cola)
  onReactivateCampaign: (campaignId: string) => Promise<void>;
  onPauseCampaign?: (campaignId: string) => Promise<void>;    // ENVÍO
  onResumeCampaign?: (campaignId: string) => Promise<void>;
  currentFilter: CampaignFilterStatus;
}

const CampaignsList: React.FC<CampaignsListProps> = ({
  campaigns,
  onEdit, // Esta prop ahora se usa para "Ver y Enviar" (que lleva a CampaignPreview)
  // onDelete, // PROP ELIMINADA
  loading,
  onArchiveCampaign,
  onReactivateCampaign,
  onPauseCampaign,
  onResumeCampaign
}) => {
  // const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null); // LÓGICA DE ELIMINACIÓN QUITADA
  const [itemActionLoading, setItemActionLoading] = useState<string | null>(null);

  const getCampaignTypeIcon = (type: CampaignType) => {
    switch (type) {
      case 'birthday': return <Calendar className="h-5 w-5 text-pink-500" />;
      case 'inactive': return <UserMinus className="h-5 w-5 text-orange-500" />;
      case 'all': return <Users className="h-5 w-5 text-blue-500" />;
      case 'custom': return <Settings className="h-5 w-5 text-purple-500" />;
      case 'bestClients': return <Trophy className="h-5 w-5 text-yellow-500" />;
      default: return <MessageSquare className="h-5 w-5 text-content-3" />;
    }
  };

  const getStatusBadgeColor = (status: CampaignStatus) => {
    switch (status) {
      case 'active': return 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300';
      case 'archived': return 'bg-surface-2 text-content-2';
      case 'draft': return 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300';
      default: return 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300';
    }
  };

  // "Archivada" era una sola etiqueta para tres cosas distintas: guardada a mano, terminada
  // sola y cancelada. Ahora las separa el vocabulario común (F3).
  const getStatusLabel = (c: Campaign) => etiquetaFicha(c.motorStatus, c.archivedAt);

  // const handleDelete = async (campaignId: string) => { ... }; // FUNCIÓN ELIMINADA

  const handleArchive = async (campaignId: string) => {
    setItemActionLoading(campaignId);
    try { await onArchiveCampaign(campaignId); }
    finally { setItemActionLoading(null); }
  };

  const handleReactivate = async (campaignId: string) => {
    setItemActionLoading(campaignId);
    try { await onReactivateCampaign(campaignId); }
    finally { setItemActionLoading(null); }
  };

  // Mismo spinner-por-fila para las acciones de envío.
  const handleAccion = async (campaignId: string, fn: (id: string) => Promise<void>) => {
    setItemActionLoading(campaignId);
    try { await fn(campaignId); }
    finally { setItemActionLoading(null); }
  };
  

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="border rounded-lg p-4 animate-pulse">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center">
                  <div className="h-5 w-5 bg-skeleton rounded-full mr-2"></div>
                  <div className="h-5 bg-skeleton rounded w-1/3"></div>
                  <div className="ml-3 h-5 bg-skeleton rounded w-1/4"></div>
                </div>
                <div className="mt-1 h-4 bg-skeleton rounded w-3/4"></div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <div className="h-6 bg-skeleton rounded-full w-24"></div>
                  <div className="h-6 bg-skeleton rounded-full w-20"></div>
                  <div className="h-6 bg-skeleton rounded-full w-28"></div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 justify-end mt-2 sm:mt-0">
                <div className="h-8 bg-skeleton rounded w-32"></div>
                <div className="h-8 bg-skeleton rounded w-28"></div>
                {/* <div className="h-8 bg-skeleton rounded w-24"></div> // Espacio para botón Eliminar quitado */}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {campaigns.map((campaign) => (
        <div  
          key={campaign.id}  
          className={`border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 ${
            campaign.status === 'active' ? 'border-green-300 bg-green-50 dark:bg-green-500/10' :
            campaign.status === 'archived' ? 'border-line bg-canvas opacity-90' :
            campaign.status === 'draft' ? 'border-yellow-300 bg-yellow-50 dark:bg-yellow-500/10' :
            'border-line bg-gray-50/30'
          }`}
        >
          <div className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center">
                  {getCampaignTypeIcon(campaign.type)}
                  <h3 className="ml-2 text-lg font-medium text-content truncate" title={campaign.name}>
                    {campaign.name}
                  </h3>
                  <span className={`ml-3 px-2.5 py-0.5 text-xs font-semibold rounded-full ${getStatusBadgeColor(campaign.status)}`}>
                    {getStatusLabel(campaign)}
                  </span>
                  {/* Modo del motor: fija (foto, un disparo) vs dinámica (regla viva) */}
                  <span
                    className={`ml-2 px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                      campaign.mode === 'dynamic'
                        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-500/20 dark:text-gray-300'
                    }`}
                    title={campaign.mode === 'dynamic'
                      ? 'Regla viva: entran y salen clientes solos; no termina hasta pausarla'
                      : 'Un solo envío: foto de la audiencia al programar'}
                  >
                    {campaign.mode === 'dynamic' ? '🔄 Dinámica' : '📌 Fija'}
                  </span>
                  {/* Canal: solo se marca el correo (WhatsApp es el default, sin ruido) */}
                  {campaign.channel === 'email' && (
                    <span
                      className="ml-2 px-2.5 py-0.5 text-xs font-semibold rounded-full bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300"
                      title="Campaña por correo electrónico"
                    >
                      📧 Correo
                    </span>
                  )}
                  {/* v4.0: badge de envío inteligente programado. Correo sin aprobar = En revisión. */}
                  {chipEnvio(campaign.motorStatus) && campaign.motorStatus !== 'draft' && (
                    campaign.channel === 'email' && !campaign.approvedAt ? (
                      <span className="ml-2 px-2.5 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">
                        🕓 En revisión
                      </span>
                    ) : (
                      <span
                        className={`ml-2 px-2.5 py-0.5 text-xs font-semibold rounded-full ${CLASE_TONO[chipEnvio(campaign.motorStatus)!.tono]}`}
                        title={chipEnvio(campaign.motorStatus)!.ayuda}
                      >
                        {chipEnvio(campaign.motorStatus)!.emoji} {chipEnvio(campaign.motorStatus)!.label}
                      </span>
                    )
                  )}
                </div>
                
                {campaign.description && (
                  <p className="mt-1 text-sm text-content-2 truncate" title={campaign.description}>{campaign.description}</p>
                )}
                
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs text-content-3">
                  <span className="flex items-center" title="Fecha de creación">
                    <Clock className="h-3.5 w-3.5 mr-1.5 text-content-3" />
                    Creada: {formatDate(campaign.createdAt)}
                  </span>
                  
                  <span className="flex items-center" title="Total de mensajes enviados para esta campaña">
                    <SendIcon className="h-3.5 w-3.5 mr-1.5 text-content-3" />
                    Total Enviados: {campaign.totalMessagesSent}
                  </span>
                  
                  {campaign.lastMessageSentAt && (
                    <span className="flex items-center" title="Fecha del último mensaje enviado">
                      <MessageSquare className="h-3.5 w-3.5 mr-1.5 text-content-3" />
                      Último Envío: {formatDate(campaign.lastMessageSentAt)}
                    </span>
                  )}
                </div>

                {/* Resultados del correo — aquí y no solo en el detalle: una campaña de
                    correo terminada (done → 'archived') no tiene botón de detalle, así
                    que esta franja es el único lugar donde el dueño ve si sirvió. */}
                {campaign.channel === 'email' && campaign.totalMessagesSent > 0 && (
                  <EmailResultados campaignId={campaign.id} size="compact" />
                )}
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 flex-shrink-0 mt-3 sm:mt-0">
                {/* --- MODIFICACIÓN: Botón "Ver y Enviar" condicional --- */}
                {(campaign.status === 'active' || campaign.status === 'draft') && (
                  <button
                    onClick={() => onEdit(campaign)} // onEdit lleva a CampaignPreview
                    disabled={!!itemActionLoading && itemActionLoading !== campaign.id}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-1.5 border border-blue-500 text-xs font-medium rounded-md shadow-sm text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50"
                  >
                    <EditIcon className="h-4 w-4 mr-1.5" /> {/* O un ícono como Play o Send */}
                    Ver y Enviar
                  </button>
                )}

                {/* ENVÍO: pausar / reanudar. Conservan la cola. Estaban solo dentro del
                    detalle; en la lista no había forma de parar un envío sin archivar. */}
                {puedePausar(campaign.motorStatus, campaign.archivedAt) && onPauseCampaign && (
                  <button
                    onClick={() => handleAccion(campaign.id, onPauseCampaign)}
                    disabled={itemActionLoading === campaign.id}
                    title={ACCIONES.pausar.title}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-1.5 border border-line text-xs font-medium rounded-md shadow-sm text-content-2 bg-surface hover:bg-amber-50 dark:hover:bg-amber-500/10 hover:text-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50"
                  >
                    {itemActionLoading === campaign.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> : <PauseCircle className="h-4 w-4 mr-1.5" />}
                    {ACCIONES.pausar.label}
                  </button>
                )}
                {puedeReanudar(campaign.motorStatus, campaign.archivedAt) && onResumeCampaign && (
                  <button
                    onClick={() => handleAccion(campaign.id, onResumeCampaign)}
                    disabled={itemActionLoading === campaign.id}
                    title={ACCIONES.reanudar.title}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-1.5 border border-line text-xs font-medium rounded-md shadow-sm text-content-2 bg-surface hover:bg-green-50 dark:hover:bg-green-500/10 hover:text-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    {itemActionLoading === campaign.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> : <PlayCircle className="h-4 w-4 mr-1.5" />}
                    {ACCIONES.reanudar.label}
                  </button>
                )}

                {/* FICHA: archivar / restaurar. No tocan la cola. */}
                {puedeArchivar(campaign.motorStatus, campaign.archivedAt) && (
                  <button
                    onClick={() => handleArchive(campaign.id)}
                    disabled={itemActionLoading === campaign.id}
                    title={ACCIONES.archivar.title}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-1.5 border border-line text-xs font-medium rounded-md shadow-sm text-content-2 bg-surface hover:bg-yellow-50 dark:hover:bg-yellow-500/10 hover:text-yellow-700 dark:text-yellow-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
                  >
                    {itemActionLoading === campaign.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> : <ArchiveIcon className="h-4 w-4 mr-1.5" />}
                    {ACCIONES.archivar.label}
                  </button>
                )}
                {puedeRestaurar(campaign.motorStatus, campaign.archivedAt) && (
                  <button
                    onClick={() => handleReactivate(campaign.id)}
                    disabled={itemActionLoading === campaign.id}
                    title={ACCIONES.restaurar.title}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-1.5 border border-green-500 text-xs font-medium rounded-md shadow-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-500/10 hover:bg-green-100 dark:hover:bg-green-500/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    {itemActionLoading === campaign.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                    {ACCIONES.restaurar.label}
                  </button>
                )}
                
                {/* --- SECCIÓN DE ELIMINAR COMPLETAMENTE QUITADA --- */}

              </div>
            </div>
            
            {/* Mensaje contextual para campañas activas/borrador */}
            {campaign.status === 'active' && (
              <div className={`mt-3 border-t px-4 py-2 ${campaign.lastMessageSentAt ? 'bg-green-50 dark:bg-green-500/10 border-green-200' : 'bg-blue-50 dark:bg-blue-500/10 border-blue-200'}`}>
                <p className={`text-xs ${campaign.lastMessageSentAt ? 'text-green-700 dark:text-green-300' : 'text-blue-700 dark:text-blue-300'}`}>
                  {campaign.lastMessageSentAt 
                    ? `Campaña activa. Última actividad de envío: ${formatDate(campaign.lastMessageSentAt)}.`
                    : campaign.totalMessagesSent > 0 
                        ? `Campaña activa. ${campaign.totalMessagesSent} mensajes enviados.`
                        : `Campaña activa y lista para su primer envío.`
                  }
                </p>
              </div>
            )}
             {campaign.status === 'draft' && (
              <div className="mt-3 bg-yellow-50 dark:bg-yellow-500/10 border-t border-yellow-200 px-4 py-2">
                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                  Esta campaña es un borrador. Actívala o edítala para comenzar a enviar mensajes.
                </p>
              </div>
            )}
             {campaign.status === 'archived' && (
              <div className="mt-3 bg-canvas border-t border-line px-4 py-2">
                <p className="text-xs text-content-2">
                  {campaign.archivedAt
                    ? 'Archivada: no envía nada, pero conserva sus mensajes enviados y pendientes. Al restaurarla vuelve tal cual.'
                    : campaign.motorStatus === 'done'
                      ? 'Terminó de enviar a toda su audiencia.'
                      : 'Cancelada: sus mensajes pendientes se descartaron. Lo enviado se conserva.'}
                </p>
              </div>
            )}

          </div>
        </div>
      ))}
    </div>
  );
};

export default CampaignsList;